/**
 * Context model — polymorphic with strict per-kind metadata schemas.
 *
 * The `metadata` field is JSONB in Postgres, but each `kind` has a strict
 * TypeScript-validated schema enforced at the API boundary. This keeps
 * `metadata` from becoming a junk drawer.
 */
import { z } from 'zod';

export const CONTEXT_KINDS = [
  'file',
  'repo',
  'mcp_server',
  'skill',
  'kb_query',
  'kb_node',
  'task',
  'person',
  'url',
] as const;
export type ContextKind = (typeof CONTEXT_KINDS)[number];

export const CONTEXT_ROLES = ['input', 'scope', 'output_target', 'reference'] as const;
export type ContextRole = (typeof CONTEXT_ROLES)[number];

// ---------------------------------------------------------------------------
// Per-kind metadata schemas
// ---------------------------------------------------------------------------

export const FileMetadataSchema = z.object({
  filename: z.string(),
  content_type: z.string(),
  size_bytes: z.number().int().nonnegative(),
});

export const RepoMetadataSchema = z.object({
  branch: z.string().default('main'),
  default_paths: z.array(z.string()).optional(),
});

export const McpServerMetadataSchema = z.object({
  tool_allowlist: z.array(z.string()).optional(),
});

export const SkillMetadataSchema = z.object({}).passthrough();

export const KbQueryMetadataSchema = z.object({
  source: z.string(),
  k: z.number().int().positive().default(10),
  groups: z.array(z.string()).optional(),
});

export const KbNodeMetadataSchema = z.object({
  source: z.string(),
});

export const TaskMetadataSchema = z.object({
  relation: z.enum(['parent', 'blocks', 'related']),
});

export const PersonMetadataSchema = z.object({
  role: z.enum(['assignee', 'reviewer', 'watcher', 'owner']),
});

export const UrlMetadataSchema = z.object({
  fetch_at_runtime: z.boolean().default(false),
});

/** Map from kind to its metadata schema. */
export const METADATA_SCHEMAS: Readonly<Record<ContextKind, z.ZodTypeAny>> = {
  file: FileMetadataSchema,
  repo: RepoMetadataSchema,
  mcp_server: McpServerMetadataSchema,
  skill: SkillMetadataSchema,
  kb_query: KbQueryMetadataSchema,
  kb_node: KbNodeMetadataSchema,
  task: TaskMetadataSchema,
  person: PersonMetadataSchema,
  url: UrlMetadataSchema,
};

/** Strictly validate context-row input by kind. */
export const ContextInputSchema = z
  .object({
    kind: z.enum(CONTEXT_KINDS),
    role: z.enum(CONTEXT_ROLES),
    ref: z.string().min(1),
    metadata: z.record(z.unknown()).default({}),
  })
  .superRefine((value, ctx) => {
    const schema = METADATA_SCHEMAS[value.kind];
    const result = schema.safeParse(value.metadata);
    if (!result.success) {
      for (const issue of result.error.issues) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['metadata', ...issue.path],
          message: issue.message,
        });
      }
    }
  });

export type ContextInput = z.infer<typeof ContextInputSchema>;
